import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';

interface WorkPlanBoardState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;

    meta: any;
}

const state: WorkPlanBoardState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,

    meta: {},
};

const initState = JSON.parse(JSON.stringify(state));

export const CLIENT_DATA = 'GET_CLIENT_DATA';

export const actions: ActionTree<WorkPlanBoardState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    loadData({ commit, getters, dispatch, state }, params) {
        const vm = (this as any)._vm;

        if (params.id == 'new') {
            state.document = {};
            state.loaded = true
        } else {
            return new Promise((resolve, reject) => {
                if (!params.silence) {
                    commit('START_LOADING');
                }

                const columns: any = {
                    id: 'client.id',
                    name: 'client.name',
                    code_1s: 'client.code_1s',

                    created_at: 'client.created_at',
                    updated_at: 'client.updated_at',
                    archived_at: 'client.archived_at',

                    category_income: 'client.category_income',
                    category_profit: 'client.category_profit',
                    category_activity: 'client.category_activity',

                    manager_id: 'client.manager_id',
                    manager_name: 'primary_manager.name',

                    secondary_manager_id: 'client.secondary_manager_id',
                    secondary_manager_name: 'secondary_manager.name',

                    delivery_warehouse_id: 'client.delivery_warehouse_id',

                    contacts: 'client.contacts',
                    primary_contact_type: 'client.primary_contact_type',

                    credit: 'client.credit',
                    competitor_trademark: 'client.competitor_trademark',
                    payment_deferment_period_id: 'client.payment_deferment_period_id',
                    source: 'client.source',

                    city_name: 'city.name',


                    payment_type_id: 'ptype.id',
                    payment_type_name: 'ptype.name',

                    total_amount: 'stats.total_amount',
                    total_weight: 'stats.total_weight',
                    entity_name: 'ent.name',

                    type_id: 'client.type_id',
                    type_code: 'ctype.code',
                    distributor_id: 'client.distributor_id',
                    distributor_name: 'distributor.name',
                    agent_id: 'client.agent_id',
                    agent_name: 'agent.name',
                    special_orders: 'client.special_orders'
                };

                vm.$db([
                    vm.$q(CLIENT_DATA)
                    .raw('documents.list')
                    .action({
                        columns,
                        from: 's:clients',
                        from_as: 'client',
                        joins: [
                          {
                            join: 's:employees',
                            type: 'left',
                            as:   'primary_manager',
                            on: {
                              $eq: ['primary_manager.id', 'client.manager_id']
                            }
                          },
                          {
                            join: 's:employees',
                            type: 'left',
                            as:   'secondary_manager',
                            on: {
                              $eq: ['secondary_manager.id', 'client.secondary_manager_id']
                            }
                          },
                          {
                            join: 's:counterparties',
                            type: 'left',
                            as:   'counterparty',
                            on: {
                              $and: [
                                {$eq: ['counterparty.client_id', 'client.id']},
                                {$eq: ['counterparty.name', {$: '123'}]} // @FIXME заменить константу
                              ]
                            }
                          },
                          {
                            join: 's:cities',
                            type: 'left',
                            as:   'city',
                            on: {
                              $eq: ['city.id', 'client.city_id']
                            }
                          },
                          {
                            join: 's:payment_types',
                            type: 'left',
                            as:   'ptype',
                            on: {
                              $eq: ['ptype.id', 'client.payment_type_id']
                            }
                          },
                          {
                            join: 's:entities',
                            type: 'left',
                            as:   'ent',
                            on: {
                              $eq: ['ent.id', 'client.entity_id']
                            }
                          },
                          {
                            join: 's:client_types',
                            type: 'left',
                            as:   'ctype',
                            on: {
                              $eq: ['ctype.id', 'client.type_id']
                            }
                          },
                          {
                            join: 's:clients',
                            type: 'left',
                            as:   'distributor',
                            on: {
                              $eq: ['distributor.id', 'client.distributor_id']
                            }
                          },
                          {
                            join: 's:agents',
                            type: 'left',
                            as:   'agent',
                            on: {
                              $eq: ['agent.id', 'client.agent_id']
                            }
                          },
                          {
                            join: {
                              columns: {
                                client_id: 'deal.client_id',
                                total_amount: { $sum: 'dcp.container_price' },
                                total_weight: { $sum: { $mul: ['pc.weight', 'dcp.container_quantity'] } },
                              },
                              from: 's:deals',
                              from_as: 'deal',
                              joins: [
                                {
                                  join: 's:deal_product_containers',
                                  type: 'left',
                                  as:   'dcp',
                                  on:   {$eq: ['dcp.deal_id', 'deal.id']}
                                },
                                {
                                  join: 's:product_containers',
                                  type: 'left',
                                  as:   'pc',
                                  on:   {$eq: ['pc.id', 'dcp.product_container_id']}
                                },
                              ],
                              where: {
                                $gte: ['deal.created_at', {$: moment().subtract('30', 'days').startOf('day').format('YYYY-MM-DD hh:mm:ss')}]
                              },
                              groups: [
                                'deal.client_id'
                              ]
                            },
                            type: 'left',
                            as: 'stats',
                            on: {
                              $and: [
                                { $eq: ['stats.client_id', 'client.id'] }
                              ]
                            }
                          }
                        ],
                        where: {
                          $eq: [`client.id`, {$: params.id}]
                        }
                    })
                ]).then(((result: ServerResult) => {
                    commit('SET_CLIENT_DATA', result.getOperation(CLIENT_DATA).getContent());

                    if (!params.silence) {
                        commit('LOADED_SUCCESS');
                    }

                    resolve();
                }));
            })
        }
    },
    toArchive({ rootState }, client_id) {
      const vm = (this as any)._vm;

      return new Promise((resolve, reject) => {
        vm.$db([
          vm.$q('PRODUCT_TO_ARCHIVE')
            .update(client_id, {
              archived_at: moment().format('YYYY-MM-DDTHH:mm:ss')
            })
        ]).then((result: ServerResult) => {
          const operationResult = result.getOperation('PRODUCT_TO_ARCHIVE');

          if (operationResult.hasError()) {
              reject();
          } else {
              resolve(operationResult.getContent());
          }
        })
        .catch((error: any) => {
            console.error('errror', error)
            reject(error);
        });
      });
  },

  toUnarchive({ rootState }, client_id) {
      const vm = (this as any)._vm;

      return new Promise((resolve, reject) => {
          vm.$db([
              vm.$q('PRODUCT_TO_ARCHIVE')
                  .update(client_id, {
                      archived_at: null
                  })
          ]).then((result: ServerResult) => {
              const operationResult = result.getOperation('PRODUCT_TO_ARCHIVE');

              if (operationResult.hasError()) {
                  reject();
              } else {
                  resolve(operationResult.getContent());
              }
          })
          .catch((error: any) => {
              console.error('errror', error)
              reject(error);
          });
      });
  },
};

export const mutations: MutationTree<WorkPlanBoardState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = initState[key]
        }
    },
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_CLIENT_DATA(state, data) {
        const schema = data.schema;
        const document = data.documents[0];

        const contactsField = schema.fields.find((field: any) => field.code === 'contacts');
        const primaryContactTypeField = schema.fields.find((field: any) => field.code === 'primary_contact_type');

        if (contactsField) {
            state.meta.contacts = { types: {} };

          for (const _type of contactsField.meta.types) {
            state.meta.contacts.types[_type.value] = _type.label;

            if(_type.type_extras) {
               for(const extraType of _type.type_extras) {
                    state.meta.contacts.types[_type.value + extraType.value] = `${_type.label} / ${extraType.label}`;
                }
            }
          }
        }

        if (primaryContactTypeField) {
            state.meta.primary_contact_types = { choices: {} };

          for (const _type of primaryContactTypeField.meta.choices) {
            state.meta.primary_contact_types.choices[_type.value] = _type.label;
          }
        }

        state.document = document
    },
};

const getters = {

};

export const STORE_KEY = '$_client_details';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
